/**
 * useLocalDatasheetColumns — column visibility + order cho các page không có
 * RTK Query preference riêng. Persist qua backend user_preferences (global
 * `/user-preferences/column-visibility`, table_key = `storageKey`).
 *
 * Trước đây hook chỉ dùng localStorage; giờ dùng RTK Query share toàn app:
 *   - 1 GET khi load app (cached)
 *   - PUT per storageKey khi user toggle / reorder cột
 *
 * Initial render hiển thị full columns; khi query resolve, state sync về
 * column visibility đã lưu (flicker ~100-200ms — chấp nhận được).
 */
import { useCallback, useEffect, useMemo, useState, type DragEvent } from 'react'

import {
  useGetUserColumnVisibilityQuery,
  usePutUserColumnVisibilityMutation,
} from '../../store/api/userColumnVisibilityApi'
import { normalizeColumnKeys } from './storage'
import type { DatasheetColumnLike, UseDatasheetColumnsMenuResult } from './types'

interface MenuState {
  order: string[]
  hidden: Set<string>
}

function buildMenuState(visibleKeys: string[], staticKeys: string[]): MenuState {
  const visibleSet = new Set(visibleKeys)
  const order: string[] = []
  const hidden = new Set<string>()
  let vi = 0
  for (const key of staticKeys) {
    if (visibleSet.has(key)) {
      order.push(visibleKeys[vi] ?? key)
      vi += 1
    } else {
      order.push(key)
      hidden.add(key)
    }
  }
  return { order, hidden }
}

export interface UseLocalDatasheetColumnsParams<TCol extends DatasheetColumnLike> {
  allColumns: TCol[]
  /** Unique key dùng để lưu visibility per bảng (vd "factory.bom.list"). */
  storageKey: string
}

export function useLocalDatasheetColumns<TCol extends DatasheetColumnLike>(
  params: UseLocalDatasheetColumnsParams<TCol>,
): UseDatasheetColumnsMenuResult<TCol> {
  const { allColumns, storageKey } = params

  const allKeys = useMemo(() => allColumns.map((c) => c.key), [allColumns])
  const columnsByKey = useMemo(
    () => new Map(allColumns.map((c) => [c.key, c] as const)),
    [allColumns],
  )

  const { data: allVisibility } = useGetUserColumnVisibilityQuery()
  const [putVisibility] = usePutUserColumnVisibilityMutation()

  const savedKeys = useMemo<string[]>(() => {
    const saved = allVisibility?.[storageKey]
    if (!saved) return [...allKeys]
    const next = normalizeColumnKeys(saved, allKeys)
    return next.length > 0 ? next : [...allKeys]
  }, [allVisibility, storageKey, allKeys])

  const [menuOrder, setMenuOrder] = useState<string[]>(() => buildMenuState(allKeys, allKeys).order)
  const [hiddenSet, setHiddenSet] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    const { order, hidden } = buildMenuState(savedKeys, allKeys)
    setMenuOrder(order)
    setHiddenSet(hidden)
  }, [savedKeys, allKeys])

  const visibleColKeys = useMemo(
    () => menuOrder.filter((k) => !hiddenSet.has(k)),
    [menuOrder, hiddenSet],
  )

  const persist = useCallback(
    (keys: string[]) => {
      void putVisibility({ tableKey: storageKey, columns: keys })
        .unwrap()
        .catch(() => undefined)
    },
    [storageKey, putVisibility],
  )

  const toggleColumn = useCallback(
    (key: string) => {
      setHiddenSet((prev) => {
        const next = new Set(prev)
        if (next.has(key)) {
          next.delete(key)
        } else {
          const visibleCount = menuOrder.length - prev.size
          if (visibleCount <= 1) return prev
          next.add(key)
        }
        const newVisible = menuOrder.filter((k) => !next.has(k))
        persist(newVisible)
        return next
      })
    },
    [menuOrder, persist],
  )

  const resetToDefault = useCallback(() => {
    setMenuOrder([...allKeys])
    setHiddenSet(new Set())
    persist([...allKeys])
  }, [allKeys, persist])

  const [dragKey, setDragKey] = useState<string | null>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)

  const onDragStart = useCallback(
    (key: string) => (e: DragEvent<HTMLElement>) => {
      setDragKey(key)
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', key)
    },
    [],
  )

  const onDragOver = useCallback(
    (key: string) => (e: DragEvent<HTMLElement>) => {
      if (!dragKey || dragKey === key) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      setDragOverKey(key)
    },
    [dragKey],
  )

  const onDrop = useCallback(
    (targetKey: string) => (e: DragEvent<HTMLElement>) => {
      e.preventDefault()
      const sourceKey = dragKey
      setDragKey(null)
      setDragOverKey(null)
      if (!sourceKey || sourceKey === targetKey) return
      setMenuOrder((prev) => {
        const from = prev.indexOf(sourceKey)
        const to = prev.indexOf(targetKey)
        if (from < 0 || to < 0) return prev
        const next = [...prev]
        const [item] = next.splice(from, 1)
        next.splice(to, 0, item)
        const newVisible = next.filter((k) => !hiddenSet.has(k))
        persist(newVisible)
        return next
      })
    },
    [dragKey, hiddenSet, persist],
  )

  const onDragEnd = useCallback(() => {
    setDragKey(null)
    setDragOverKey(null)
  }, [])

  const visibleColumns = useMemo(
    () =>
      visibleColKeys.flatMap((k) => {
        const col = columnsByKey.get(k)
        return col ? [col] : []
      }),
    [columnsByKey, visibleColKeys],
  )

  const menuItems = useMemo(
    () =>
      menuOrder.flatMap((k) => {
        const col = columnsByKey.get(k)
        return col ? [col] : []
      }),
    [columnsByKey, menuOrder],
  )

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const openMenu = useCallback((anchor: HTMLElement) => {
    setMenuAnchor(anchor)
    // Blur trigger sau khi giữ anchor: tránh warning "aria-hidden on focused
    // element" (MUI v5) — Popover áp aria-hidden lên #root trước khi focus-trap
    // dời focus khỏi nút mở. anchorEl là DOM node nên vẫn anchor đúng sau blur.
    anchor.blur()
  }, [])
  const closeMenu = useCallback(() => setMenuAnchor(null), [])

  return {
    visibleColumns,
    menuItems,
    hiddenSet,
    menuAnchor,
    openMenu,
    closeMenu,
    toggleColumn,
    resetToDefault,
    dragKey,
    dragOverKey,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
  }
}
